/**
 * Reservation Store
 * Manages reservation creation flow and state
 */

import {
	calculateAdditionalServicesTotal,
	getReservationDays,
} from '@/lib/reservation-pricing'
import { formatDateForApi } from '@/lib/utils'
import type {
	Car,
	CreateReservationDTO,
	ReservationDates,
	ReservationExtras,
	ReservationLocation,
} from '@/types'
import type { SearchRequestModel } from '@/types/api-models'
import { addDays } from 'date-fns'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface ReservationState {
	// Search parameters for finding cars
	searchParams: SearchRequestModel

	// Current reservation being created
	currentReservation: Partial<CreateReservationDTO> | null

	// Selected car details
	selectedCar: Car | null

	/**
	 * Indica si el usuario ha seleccionado el seguro TWH como extra opcional.
	 * Cuando es true, el payload de reserva usa selectedCar.twhOffer.groupID.
	 */
	twhSelected: boolean

	// Step management for multi-step form
	currentStep: number

	// Actions
	setSearchParams: (params: Partial<SearchRequestModel>) => void
	setCar: (car: Car) => void
	setCarId: (carId: string) => void
	setDates: (dates: ReservationDates) => void
	setLocation: (location: ReservationLocation) => void
	setExtras: (extras: ReservationExtras) => void
	setPaymentMethod: (method: CreateReservationDTO['paymentMethod']) => void
	setNotes: (notes: string) => void
	setDiscountCode: (code: string) => void
	toggleAdditionalService: (serviceId: string) => void
	setTwhSelected: (selected: boolean) => void
	nextStep: () => void
	previousStep: () => void
	setStep: (step: number) => void
	resetReservation: () => void

	// Helper methods
	isStepValid: (step: number) => boolean
	getTotalWithExtras: () => number
}

// Default search params (example: next day pickup)
const today = new Date()
const tomorrow = addDays(today, 1)
const nextWeek = addDays(tomorrow, 7)

const defaultSearchParams: SearchRequestModel = {
	pickupDate: formatDateForApi(tomorrow),
	pickupStationId: 'BOGT01',
	dropoffDate: formatDateForApi(nextWeek),
	dropoffStationId: 'BOGT01',
	driverAge: 25,
	countryCode: 'CO',
}

export const useReservationStore = create<ReservationState>()(
	devtools(
		(set, get) => ({
			searchParams: defaultSearchParams,
			currentReservation: null,
			selectedCar: null,
			twhSelected: false,
			currentStep: 1,

			setSearchParams: params =>
				set(state => ({
					searchParams: { ...state.searchParams, ...params },
				})),

			setCar: car =>
				set(state => ({
					selectedCar: car,
					twhSelected: false,
					currentReservation: { ...state.currentReservation, carId: car.id },
				})),

			setCarId: carId =>
				set(state => ({
					currentReservation: { ...state.currentReservation, carId },
				})),

			setTwhSelected: selected => set({ twhSelected: selected }),

			setDates: dates =>
				set(state => ({
					currentReservation: { ...state.currentReservation, dates },
				})),

			setLocation: location =>
				set(state => ({
					currentReservation: { ...state.currentReservation, location },
				})),

			setExtras: extras =>
				set(state => ({
					currentReservation: { ...state.currentReservation, extras },
				})),

			setPaymentMethod: paymentMethod =>
				set(state => ({
					currentReservation: { ...state.currentReservation, paymentMethod },
				})),

			setNotes: notes =>
				set(state => ({
					currentReservation: { ...state.currentReservation, notes },
				})),

			setDiscountCode: code =>
				set(state => ({
					currentReservation: {
						...state.currentReservation,
						extras: {
							...state.currentReservation?.extras,
							discountCode: code,
						} as ReservationExtras,
					},
				})),

			toggleAdditionalService: serviceId =>
				set(state => {
					const extras = state.currentReservation?.extras
					const services = extras?.additionalServices || []
					const updatedServices = services.map(s =>
						s.id === serviceId ? { ...s, selected: !s.selected } : s,
					)

					return {
						currentReservation: {
							...state.currentReservation,
							extras: {
								...extras,
								additionalServices: updatedServices,
							} as ReservationExtras,
						},
					}
				}),

			nextStep: () =>
				set(state => ({
					currentStep: Math.min(state.currentStep + 1, 4),
				})),

			previousStep: () =>
				set(state => ({
					currentStep: Math.max(state.currentStep - 1, 1),
				})),

			setStep: step => set({ currentStep: step }),

			resetReservation: () => set({ currentReservation: null, currentStep: 1 }),

			isStepValid: step => {
				const { currentReservation } = get()
				if (!currentReservation) return false

				switch (step) {
					case 1:
						return !!currentReservation.carId
					case 2:
						return !!currentReservation.dates
					case 3:
						return !!currentReservation.location
					case 4:
						return !!currentReservation.paymentMethod
					default:
						return false
				}
			},

			getTotalWithExtras: () => {
				const { currentReservation, searchParams } = get()
				const reservationDays = getReservationDays(
					searchParams.pickupDate,
					searchParams.dropoffDate,
				)

				if (currentReservation?.extras?.additionalServices?.length) {
					return calculateAdditionalServicesTotal(
						currentReservation.extras.additionalServices,
						reservationDays,
					)
				}

				// Prefer using selectedExtras (ExtraModel[]) if available
				if (currentReservation?.extras?.selectedExtras) {
					return currentReservation.extras.selectedExtras.reduce(
						(sum, extra) => sum + (extra.dailyPrice || 0) * reservationDays,
						0,
					)
				}

				return 0
			},
		}),
		{ name: 'ReservationStore' },
	),
)
